import {createDraftRepository} from '@yandex-delivery/api/repo/draft'
import {useFormContext} from '@yandex-delivery/form'
import type {OrderFormState} from '@yandex-delivery/form/ui/OrderForm/types'
import {useDebouncedFunction} from '@yandex-delivery/hooks'
import {useCallback, useMemo} from 'react'

import {appConfig} from 'src/shared/config'

import {store} from '../store'

/** Ответ хука useDefaultValuesAndDraftSync */
type useDefaultValuesAndDraftSyncResponse = {
  /** Дефолтные значения для формы */
  defaultValues: OrderFormState | undefined
  /** Функция для удаления всех значений из persistent storage */
  clearDraftValues: VoidFunction
  /** Функция для обновления значений в persistent storage */
  updateDraftValues: (values: OrderFormState) => void
}

/** Время до обновления данных в сторе после очередного вызова updateValues */
const DEBOUNCE_WAIT_TIME_IN_MS = 100

/**
 * Возвращает номер телефона из паспорта или пустую строку при отсутствии телефона
 */
const getDefaultPhone = () => {
  let defaultPhone = appConfig.launch?.passport_account?.details?.phones?.[0]?.phone || ''

  // Если первый символ - плюс, вырезаем его из строки
  if (defaultPhone[0] === '+') {
    defaultPhone = defaultPhone.substring(1)
  }

  return defaultPhone
}

export const {fetchClearDraftSync, fetchPersistDraftSync, fetchRestoreDraftSync} =
  createDraftRepository<OrderFormState>({
    key: '__ya_delivery_order_form_state_bdui_mobile__',
  })

/**
 * Синкает драфтовые значения между persistent storage и локальным стором.
 * Если пользователь что-то внёс в форму заказа во время предыдущего захода,
 * при этом не создал заказ, эти данные сохранятся.
 * @returns Дефолтные значения для формы и функция для обновления этих данных в persistent storage
 */
export const useDefaultValuesAndDraftSync = (): useDefaultValuesAndDraftSyncResponse => {
  /** Методы для управления данными в persistent storage */

  const vertical = useFormContext().watch('offerFlow.vertical.name')

  const defaultValues = useMemo(() => {
    const values: OrderFormState = fetchRestoreDraftSync() || {}
    /** получаем из конфига дефолтные значения, которые не может у себя обработать бэкенд */
    const defaults =
      store.state.settings && Object.keys(store.state.settings).includes(vertical)
        ? store.state.settings[vertical].defaultValues
        : {}

    if (!values.contacts) {
      values.contacts = [{}]
    }

    /** Если телефон не заполнен, кладём туда первый телефон из паспорта */
    values.contacts[0].phone ||= getDefaultPhone()

    /**
     * Форма может вместо незаполненных структур выставлять null.
     * Фильтруем контакты, чтобы пустые сущности не оказались в форме
     * */
    if (values?.contacts) {
      values.contacts = values.contacts.filter(Boolean)
    }

    /** подмешиваем в стейт значения из черновика */
    store.setState(prev => ({
      ...prev,
      formRequest: {
        ...prev.formRequest,
        state: {
          ...prev.formRequest.state,
          ...defaults,
          ...values,
        },
      },
    }))

    return {...defaults, ...values}
  }, [vertical])

  const updateValues = useCallback((values: OrderFormState) => {
    // Удаляем поля, которые не нужно сохранять в драфт
    const {payment_info: _paymentInfo, ndd_offer_name: _ndd_offer_name, ...valuesToSave} = values

    fetchPersistDraftSync(valuesToSave)
  }, [])

  const updateValuesDebounced = useDebouncedFunction(updateValues, DEBOUNCE_WAIT_TIME_IN_MS)

  return {defaultValues, clearDraftValues: fetchClearDraftSync, updateDraftValues: updateValuesDebounced}
}
